import React, {useEffect, useRef, useState} from "react";
import {TrainingAPI} from "@api/TrainingAPI";
import {audioMessage as AntdMessage} from "@components/Common/Notifications/audioMessage";
import {FaRegThumbsDown, FaThumbsDown} from "react-icons/fa";
import ReactMarkdown from "react-markdown";
import {useChatbotContext} from "@components/Chatbot/context";
import {getMessageColor, isAIMessage, isNotDefaultIntroMessage} from "@components/Chatbot/utils";
import {useSelector} from "react-redux";
import {Layout, Popover, Tooltip} from "antd";
import spinner from "@images/chatbot-spinner.svg";
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts";
import SummaryCard from "@components/Chatbot/Summary/SummaryCard";
import PlanEditorPanel from "@components/Chatbot/PlanEditor";
import Sider from "antd/es/layout/Sider";
import {Content, Footer} from "antd/es/layout/layout";
import MessageInfoDrawer from "@components/Chatbot/MessageDrawer";
import {BsGear} from "react-icons/bs";
import {AutocompleteTextarea} from "@components/Common/AutocompleteTextarea";
import ChatbotIntroOverlay from "@components/Chatbot/ChatbotIntroOverlay";

let trainingAPI = new TrainingAPI();

const TypingIndicator = () => {
    /* Includes the typing dots and the intermediate messages */

    const { info: { team_colors: teamColors } } = useSelector(({ team }) => team);
    const { isTyping, intermediateMessages } = useChatbotContext();

    const intermediateMessageToRender = () => {
        // render last intermediate message
        if (intermediateMessages.length === 0) return null;
        return intermediateMessages[intermediateMessages.length - 1];
    }

    return (
        <div
            className="chat-message-bubble chat-message-bubble-intermediate"
            style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                alignItems: "center",
                alignSelf: "flex-start",
                opacity: 0.7,
                fontStyle: "italic",
                backgroundColor: teamColors.primary,
                color: teamColors.font_colors?.length ? teamColors.font_colors[0] : "white",
                fontSize: '0.8rem'
            }}
        >
            {
                isTyping && (
                    <div>
                        <img src={spinner} alt="Loading…" className="chat-typing-spinner" />
                    </div>
                )}
            <div style={{ marginLeft: 8 }}>
                {intermediateMessageToRender() ? intermediateMessageToRender()?.message?.content : ""}
            </div>
        </div>
    )
}


const MessageReviewInput = ({ message }) => {
    /*
    Where beta users can review messages = mark bad AI messages + add notes/corrections
     */
    const { messages } = useChatbotContext();
    const [toggleReview, setToggleReview] = useState(false);
    const [reviewNotes, setReviewNotes] = useState('');

    const onThumbDownClick = () => {
        setToggleReview(!toggleReview);
    }

    const submitMessageForReview = async () => {
        try {
            // Send message to backend for review
            const res = trainingAPI.reviewAIMessage(message?.content?.message, message?.metadata, messages, reviewNotes);
            if (res) {
                await AntdMessage.success("AI Message Reviewed");
                setToggleReview(false);

            } else {
                console.error("Failed to submit message for review");
            }
        } catch (e) {
            console.error("Error submitting message for review: ", e);
        }
    }

    return (
        <div className="chat-message-review">
            <Tooltip
                placement={"bottomLeft"}
                title={"Review AI Message"}
            >
                <Popover
                    content={
                        <>
                        <textarea
                            className="chat-message-review-textarea"
                            value={reviewNotes}
                            onChange={(e) => setReviewNotes(e.target.value)}
                            placeholder="(optional) How should the AI respond?"
                        />
                            <button
                                className="chat-message-review-submit"
                                onClick={submitMessageForReview}
                            >
                                Review AI Message
                            </button>
                        </>
                    }
                    trigger="click"
                    placement="bottomLeft"
                    open={toggleReview}
                    onOpenChange={(open) => {
                        if (!open) {
                            setToggleReview(false);
                        }
                    }}
                >
                    {
                        toggleReview ? (
                            <FaThumbsDown
                                className="chat-message-review-icon"
                                onClick={onThumbDownClick}
                            />
                        ) : (
                            <FaRegThumbsDown
                                className="chat-message-review-icon"
                                onClick={onThumbDownClick}
                            />
                        )
                    }
                </Popover>
            </Tooltip>

        </div>
    )
}


const FollowUpMessageButtons = ({ onClick }) => {
    const { followUpMessages, isTyping, followUpLoading } = useChatbotContext();
    const isLoading = followUpLoading || isTyping;

    const handleClick = (message) => {
        onClick(message?.question);
    }

    if (isLoading === undefined || followUpMessages === undefined || followUpMessages.length === 0) {
        return (
            <></>
        )
    }

    return (
        <div className="chat-followup-messages-container">
            {
                isLoading ? (
                    <></>
                ) : (
                    followUpMessages?.map((message, _) => (
                        <div
                            key={message?.key}
                            className="chat-followup-message-button"
                            onClick={() => handleClick(message)}
                        >
                            <p className="chat-followup-message-text">{message?.key}</p>
                        </div>
                    ))
                )
            }
        </div>
    )
}


const MessageIconPanel = ({ children, message }) => {
    const {
        setSelectedMessage,
    } = useChatbotContext();

    return (
        <div
            style={{
                alignSelf: isAIMessage(message) ? "flex-start" : "flex-end",
                alignItems: "flex-start",
                display: "flex",
                flexDirection: "column",
            }}
        >
            {children}
            <div className="chat-message-icon-panel">
                {
                    isAIMessage(message) && isNotDefaultIntroMessage(message)
                        ? <MessageReviewInput message={message} />
                        : <></>
                }
                {
                    isAIMessage(message) && isNotDefaultIntroMessage(message)
                        ? (
                            <Tooltip
                                placement={"bottomLeft"}
                                title={"Edit Message Parameters"}
                            >
                                <BsGear
                                    size={20}
                                    onClick={() => setSelectedMessage(message)}
                                />
                            </Tooltip>
                        )
                        : <></>
                }
            </div>
        </div>
    )
}


const MessageBubble = ({ children, message }) => {
    const {
        info: { team_colors: teamColors },
    } = useSelector(({ team }) => team);

    const {
        setSelectedMessage,
        isMessageSelected,
    } = useChatbotContext();

    return (
        <div
            className="chat-message-bubble-wrapper"
            style={{
                alignSelf: isAIMessage(message) ? "flex-start" : "flex-end",
            }}
        >
            <div
                className={"chat-message-bubble " + (isMessageSelected(message) ? "chat-message-selected" : "")}
                style={{
                    backgroundColor: isAIMessage(message) ? teamColors.primary : teamColors.secondary,
                    color: getMessageColor(teamColors, message),
                    cursor: isAIMessage(message) && isNotDefaultIntroMessage(message) ? "pointer" : "default",
                }}
                onClick={() => setSelectedMessage(message)}
            >
                {children}
            </div>
        </div>
    )
}

const TextMessage = ({ message }) => {
    const messageText = message?.content?.message || message?.message || "";
    return (
        <MessageBubble message={message} key={message.id}>
            <ReactMarkdown className="chat-message-text">
                {messageText}
            </ReactMarkdown>
        </MessageBubble>
    );
}

const DummyMessage = ({ message }) => {
    return (
        <MessageBubble message={message}>
            <p className="chat-message-text">
                {message.message || "This is a dummy message. No content available."}
            </p>
        </MessageBubble>
    );
}


const TableMessage = ({ message }) => {
    const { info: { team_colors: teamColors } } = useSelector(({ team }) => team);

    function snakeToTitleCase(str) {
        return str
            .split(/[_ ]+/)
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    }

    const renderTable = (headers, data) => {
        if (!data || data.length === 0) return null;

        return (
            <table className="chat-interface-table">
                <thead className="chat-interface-table-head">
                <tr className="chat-interface-table-header-tr">
                    {headers?.map((header, index) =>
                        <th
                            key={index}
                            className="chat-interface-th"
                            style={{
                                backgroundColor: teamColors.secondary,
                                color: teamColors.font_colors?.length > 1 ? teamColors.font_colors[1] : "white",
                                padding: "10px"
                            }}
                        >
                            {snakeToTitleCase(header)}
                        </th>
                    )}
                </tr>
                </thead>
                <tbody className="chat-interface-table-body">
                {data.map((row, rowIndex) => (
                    <tr
                        key={rowIndex}
                        className="chat-interface-table-body-tr"
                    >
                        {row.map((cell, cellIndex) =>
                            <td
                                key={cellIndex}
                                className="chat-interface-td"
                                style={{
                                    borderColor: teamColors.font_colors?.length > 1 ? teamColors.font_colors[1] : "white",
                                    borderColorOpacity: 0.8,
                                    textAlign: (cellIndex === 0 ? 'left' : 'right')
                                }}
                            >
                                {cell}
                            </td>)}
                    </tr>
                ))}
                </tbody>
            </table>
        );
    };

    return (
        <>
            <TextMessage message={message} />
            <MessageBubble message={message}>
                {renderTable(message.content.result.column_names, message.content.result.data)}
            </MessageBubble>
        </>

    )
}

const ChartMessage = ({ message }) => {
    const handleTooltipFunction = (functionString) => {
        if (!functionString) return null;
        // const bodyMatch = functionString.match(/function\s*\(\)\s*\{([\s\S]*)\}/);
        //
        // if (bodyMatch && bodyMatch[1]) {
        //     const functionBody = bodyMatch[1].trim();
        //     return new Function(functionBody);
        // }
        return null;
    }

    const formatChartOptions = (chartData) => {
        return {
            ...chartData,
            ...(
                chartData?.tooltip ? {
                    tooltip: {
                        ...chartData.tooltip,
                        // formatter: new Function(chartData?.tooltip?.formatter)
                        formatter: handleTooltipFunction(chartData?.tooltip?.formatter)
                    }
                } : {})
        }
    }

    return (
        <>
            <TextMessage message={message} />
            <MessageBubble message={message}>
                <HighchartsReact highcharts={Highcharts} options={formatChartOptions(message?.content?.chart || {})} />
            </MessageBubble>
        </>

    )
}

const SummaryMessage = ({ message }) => {
    return (
        <>
            <TextMessage message={message} />
            <MessageBubble message={message}>
                <SummaryCard message={message} />
            </MessageBubble>
        </>
    )
}

const MessageDisplay = ({ sendMessage }) => {
    const {
        messages,
        followUpMessages,
        isTyping,
        setFollowUpMessages,
        setFollowUpLoading,
        drawerOpen
    } = useChatbotContext();
    const bottomRef = useRef(null);
    const contentRef = React.useRef(null);
    const footerRef = React.useRef(null);
    const prevMessagesLengthRef = useRef(messages.length);

    const useCssVarFromHeight = ({ containerRef, targetRef, varName }) => {
        useEffect(() => {
            const container = containerRef.current;
            const target = targetRef.current;
            if (!container || !target) return;

            const ro = new ResizeObserver(() => {
                const h = target.getBoundingClientRect().height;
                container.style.setProperty(varName, `${Math.ceil(h)}px`);
            });

            ro.observe(target);
            // initialize immediately
            const h = target.getBoundingClientRect().height;
            container.style.setProperty(varName, `${Math.ceil(h)}px`);

            return () => ro.disconnect();
        }, [containerRef, targetRef, varName]);
    }

    useEffect(() => {
        if (bottomRef.current) {
            const isConversationLoad = messages.length - prevMessagesLengthRef.current > 1;
            bottomRef.current.scrollIntoView({ behavior: isConversationLoad ? "instant" : "smooth" });
        }
        prevMessagesLengthRef.current = messages.length;
    }, [messages, followUpMessages]);

    useCssVarFromHeight({
        containerRef: contentRef,
        targetRef: footerRef,
        varName: "--chat-footer-h",
    });

    const renderMessage = (message) => {
        const messageType = message?.content?.type || message?.type || 'text'; // Default to 'text' if type is not defined
        switch (messageType) {
            case 'text':
                return <TextMessage message={message} />;
            case 'single_value':
                return <TextMessage message={message} />;
            case 'table':
                return <TableMessage message={message} />;
            case 'summary':
                return <SummaryMessage message={message} />;
            case 'chart':
                return <ChartMessage message={message} />;
            // Add more cases as needed for different message types
            default:
                return <DummyMessage message={message} />;  // Default to TextMessage if no type is matched
        }
    };

    const onClickFollowUpMessage = (question) => {
        setFollowUpMessages([]);
        setFollowUpLoading(false);
        sendMessage(question, 'user');
    }

    const showIntro = messages.length <= 1;

    return (
        <Layout className="chat-interface-container">
            <Content className="chat-messages-container" ref={contentRef}>
                <div className="chat-messages-wrapper">
                    <ChatbotIntroOverlay show={showIntro} />
                    <div className="chat-messages-inner">
                        {messages.map(message => (
                            <React.Fragment key={message?.id}>
                                <MessageIconPanel message={message}>
                                    {renderMessage(message)}
                                </MessageIconPanel>
                            </React.Fragment>
                        ))}
                        {isTyping ? <TypingIndicator /> : null}
                        <div className="chat-messages-pinned-bottom">
                            <FollowUpMessageButtons onClick={onClickFollowUpMessage} />
                        </div>
                        <div ref={bottomRef} />
                    </div>
                </div>
                <div className="chat-messages-footer" ref={footerRef}>
                    {/* glass ONLY for input */}
                    <div className="chat-input-glass">
                        <Footer className="chat-footer">
                            <AutocompleteTextarea
                                onSendMessage={sendMessage}
                                isTyping={false}
                                entityTypes={['players', 'metrics', 'positions', 'playerGroups', 'treatments', 'treatmentApplicationTime']}
                                maxSuggestions={10}
                                rows={2}
                                placeholder="Ask me a question..."
                                containerClassName="chat-send-message-input-container"
                                className="chat-send-message-input"
                                buttonClassName="chat-send-message-button"
                                theme="search"
                            />
                        </Footer>
                    </div>
                </div>
            </Content>
            <MessageInfoDrawer />
        </Layout>
    )
}

export default MessageDisplay;
